import baseApi from './base';
import {
  getPublishedEntityLocation,
  PublishedEntityLocationInterface,
  transformElearningThemeAssetLinks,
} from '@/lib/util';
import config from '@/lib/config';
import { FrameDataType } from '@/modules/types/frame';
import { ElearningDataType } from '@/modules/types/elearning';
import { QuizDataType } from '@/modules/types/quiz';
import { FaqType, HelperType } from '@/modules/types/helper';

const BASE_URL = `${config.api.baseUrl}`;
const PUBLISHED_ASSETS = 'published-content-assets' as const;

export interface ElearningJsonData extends ElearningDataType {
  frames?: FrameDataType[];
}

export interface HelpJsonData {
  content: HelperType[];
  faqs: FaqType[];
}

/**
 * Published playback must never reference /content/ (draft assets sit behind
 * authoring-only signed cookies — LMS2-4987). Both transforms below rewrite
 * every draft URL embedded in the published JSON to the published copy and
 * stamp assetBasePath so filename-built URLs (resolveAssetUrl) follow suit.
 */
export const transformElearningJson = (
  elearningData: ElearningJsonData,
  args: PublishedEntityLocationInterface,
): ElearningJsonData => {
  const elearning = { ...elearningData };

  const publishedAssetLink = getPublishedEntityLocation({
    ...args,
    contentType: PUBLISHED_ASSETS,
  });
  const resources = elearning.resources?.map((resource) => {
    const { url } = resource.elearningResourceMetadata;
    const regexPattern = new RegExp(
      `/content/${args.tenantId}/elearnings/${args.entityId}`,
    );

    const updatedUrl = url.replace(regexPattern, `${publishedAssetLink}`);
    return {
      ...resource,
      elearningResourceMetadata: {
        ...resource.elearningResourceMetadata,
        url: updatedUrl.includes('/')
          ? updatedUrl
          : `${publishedAssetLink}/pages/resources/${url}`,
      },
    };
  });

  // @TODO: replace all entities -  just use one replace checking all entities in one place -> in code as well as in our canvas
  if (elearningData.theme) {
    const publishedThemeAssetLink = getPublishedEntityLocation({
      ...args,
      entity: 'themes',
      entityId: elearningData.theme.id,
      contentType: PUBLISHED_ASSETS,
    });

    const theme = transformElearningThemeAssetLinks(
      elearningData.theme,
      `${publishedThemeAssetLink}/pages/images`,
    );

    elearning.theme = theme;
  }

  // Serialize-and-replace so every HTML-bearing frame field is covered
  // (content, mobileContent, elements[].content tooltips) — not just content.
  const draftAssetBase = `/content/${args.tenantId}/elearnings/${args.entityId}`;
  const frames = elearning.frames
    ? (
        JSON.parse(
          JSON.stringify(elearning.frames)
            .split(draftAssetBase)
            .join(publishedAssetLink),
        ) as FrameDataType[]
      ).map((frame) => ({ ...frame, assetBasePath: publishedAssetLink }))
    : elearning.frames;

  return {
    ...elearning,
    resources,
    frames,
    assetBasePath: publishedAssetLink,
  };
};

export const transformQuizJson = (
  quizData: QuizDataType,
  args: PublishedEntityLocationInterface,
): QuizDataType => {
  const assetBasePath = getPublishedEntityLocation({
    ...args,
    contentType: PUBLISHED_ASSETS,
  });

  // Quiz uploads land under /content/{tenant}/quizzes/{elearningId}/ —
  // serialize-and-replace covers question/choice/feedback/result HTML at once.
  const draftAssetBase = `/content/${args.tenantId}/quizzes/${args.entityId}`;
  const quiz = JSON.parse(
    JSON.stringify(quizData).split(draftAssetBase).join(assetBasePath),
  ) as QuizDataType;

  return {
    ...quiz,
    assetBasePath,
    questions: quiz.questions?.map((question) => ({
      ...question,
      assetBasePath,
    })),
  };
};

const publishedEntityApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getElearningJson: build.query<
      ElearningJsonData,
      PublishedEntityLocationInterface
    >({
      query: (params) => {
        const elearningJsonPath = getPublishedEntityLocation(params);
        return {
          // TODO: Update to do some checking to ensure valid user can access the function
          url: `${BASE_URL}${elearningJsonPath}/elearning.json`,
        };
      },
      transformResponse: (elearningData: ElearningJsonData, meta, args) =>
        transformElearningJson(elearningData, args),
      providesTags: [],
    }),
    getQuizJson: build.query<QuizDataType, PublishedEntityLocationInterface>({
      query: (params) => {
        const quizJsonPath = getPublishedEntityLocation(params);
        return {
          // TODO: Update to do some checking to ensure valid user can access the function
          url: `${BASE_URL}${quizJsonPath}/quiz.json`,
        };
      },
      transformResponse: (quizData: QuizDataType, meta, args) =>
        transformQuizJson(quizData, args),
      providesTags: [],
    }),
    getHelpJson: build.query<HelpJsonData, undefined>({
      query: () => ({
        // TODO: Update to do some checking to ensure valid user can access the function
        url: `${BASE_URL}/published-common-content/help.json`,
      }),
      providesTags: [],
    }),
  }),
});

export const {
  useGetElearningJsonQuery,
  useLazyGetElearningJsonQuery,
  useGetQuizJsonQuery,
  useGetHelpJsonQuery,
} = publishedEntityApi;
export default publishedEntityApi;
